import { useForm } from "@tanstack/react-form";
import { useEffect, useId, useRef } from "react";

import { t } from "../../../i18n";
import { createSettlementSchema } from "../../../lib/schemas";
import { trpc } from "../../../lib/trpc";
import { fieldError } from "../../../lib/validation";
import { Button, Dialog, Field, FieldMessage, Input, Stack } from "../../ui";
import { currentMonthDefaults, lastDayOfMonthIso } from "../utils";

interface CreateSettlementDialogProps {
  householdId: string;
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

export function CreateSettlementDialog({
  householdId,
  open,
  onClose,
  onCreated,
}: CreateSettlementDialogProps) {
  const utils = trpc.useUtils();
  const create = trpc.settlement.create.useMutation({
    onSuccess: (data) => {
      utils.settlement.list.invalidate({ groupId: householdId });
      onCreated(data.id);
    },
  });

  const lastOfMonth = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1, 0);
    return d.toISOString().slice(0, 10);
  })();

  const endDateTouched = useRef(false);

  const form = useForm({
    defaultValues: currentMonthDefaults(),
    validators: {
      onChange: createSettlementSchema,
      onSubmit: createSettlementSchema,
    },
    onSubmit: ({ value }) =>
      create.mutate({
        groupId: householdId,
        title: value.title.trim(),
        startDate: value.startDate,
        endDate: value.endDate,
      }),
  });

  useEffect(() => {
    if (open) {
      form.reset(currentMonthDefaults());
      endDateTouched.current = false;
    }
  }, [open, form]);

  const formId = useId();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t.household.settlements.createDialog.title}
      description={t.household.settlements.createDialog.description}
      footer={
        <>
          <Button onClick={onClose}>{t.common.cancel}</Button>
          <Button type="submit" form={formId} variant="primary" isLoading={create.isPending}>
            {t.common.create}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <Stack gap={4}>
          <form.Field name="title">
            {(field) => (
              <Field
                label={t.household.settlements.createDialog.titleLabel}
                required
                error={fieldError(field)}
              >
                <Input
                  autoFocus
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t.household.settlements.createDialog.titlePlaceholder}
                  invalid={!!fieldError(field)}
                />
              </Field>
            )}
          </form.Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <form.Field name="startDate">
              {(field) => (
                <Field
                  label={t.household.settlements.createDialog.startDateLabel}
                  required
                  error={fieldError(field)}
                >
                  <Input
                    type="date"
                    value={field.state.value}
                    max={lastOfMonth}
                    onChange={(e) => {
                      const next = e.target.value;
                      field.handleChange(next);
                      if (!endDateTouched.current) {
                        const last = lastDayOfMonthIso(next);
                        if (last) form.setFieldValue("endDate", last);
                      }
                    }}
                    onBlur={field.handleBlur}
                    invalid={!!fieldError(field)}
                  />
                </Field>
              )}
            </form.Field>
            <form.Field name="endDate">
              {(field) => (
                <Field
                  label={t.household.settlements.createDialog.endDateLabel}
                  required
                  error={fieldError(field)}
                >
                  <Input
                    type="date"
                    value={field.state.value}
                    onChange={(e) => {
                      endDateTouched.current = true;
                      field.handleChange(e.target.value);
                    }}
                    onBlur={field.handleBlur}
                    invalid={!!fieldError(field)}
                  />
                </Field>
              )}
            </form.Field>
          </div>
          {create.isError && (
            <FieldMessage tone="error" className="text-sm">
              {create.error.message}
            </FieldMessage>
          )}
        </Stack>
      </form>
    </Dialog>
  );
}
